import React, { useEffect, useState } from "react";
import { PageShell } from "@/pages/dashboard/RolePages";
import { Clock, CheckCircle2, XCircle, RotateCcw, Inbox, MessageCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const STATUS_META = {
  pending:   { label: "Pendiente de aprobación", color: "#D4AF37", Icon: Clock },
  approved:  { label: "Aprobada", color: "#1FA774", Icon: CheckCircle2 },
  rejected:  { label: "Rechazada", color: "#7A0E17", Icon: XCircle },
  more_info: { label: "Información adicional solicitada", color: "#A855F7", Icon: MessageCircle },
  none:      { label: "No solicitada", color: "#525252", Icon: Inbox },
};

const KINDS = [
  { kind: "consultant", title: "Consultor KD9",         desc: "Permite trabajar consultas y diagnósticos asignados.", endpoint: "/api/role-requests/consultant" },
  { kind: "creator",    title: "Creador Marketplace",  desc: "Permite publicar productos en el marketplace KD9.",   endpoint: "/api/role-requests/creator" },
];

const MisSolicitudes = () => {
  const [byKind, setByKind] = useState({ consultant: null, creator: null });
  const [error, setError] = useState("");
  const [version, setVersion] = useState(0);
  const [consultantPhotoData, setConsultantPhotoData] = useState("");
  const [consultantPhotoPreview, setConsultantPhotoPreview] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/api/role-requests/mine");
        const latest = { consultant: null, creator: null };
        for (const r of data) {
          if (!latest[r.request_type] || new Date(r.created_at) > new Date(latest[r.request_type].created_at)) {
            latest[r.request_type] = r;
          }
        }
        if (!cancelled) { setByKind(latest); setError(""); }
      } catch (e) {
        if (!cancelled) setError(formatApiError(e));
      }
    })();
    return () => { cancelled = true; };
  }, [version]);

  const reload = () => setVersion((v) => v + 1);

  const send = (kind, endpoint) => {
    if (kind === "consultant" && !consultantPhotoData) {
      setError("Debes seleccionar una fotografía frontal oficial.");
      return;
    }
    const notes = window.prompt(
      kind === "consultant"
        ? "Cuéntanos brevemente tu experiencia como consultor:"
        : "Cuéntanos brevemente sobre tu marca / productos:",
      ""
    );
    if (notes === null) return;
    return api
      .post(endpoint, { notes, photo_data: kind === "consultant" ? consultantPhotoData : null, consultant_type: "consultant" })
      .then(reload)
      .catch((e) => setError(formatApiError(e)));
  };

  return (
    <PageShell
      kicker="Usuario · Solicitudes"
      title="MIS SOLICITUDES DE ROL"
      desc="Cualquier cuenta puede solicitar ser consultor o creador. Mientras la solicitud se revisa, sigues navegando como Usuario."
      testId="mis-solicitudes-page"
    >
      {error && (
        <div
          className="kd9-card p-4 mb-4 text-sm font-mont"
          style={{ color: "#FCA5A5", borderColor: "rgba(122,14,23,0.4)", background: "rgba(122,14,23,0.10)" }}
          data-testid="mis-solicitudes-error"
        >
          {error}
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-5">
        {KINDS.map((it) => {
          const req = byKind[it.kind];
          const status = req?.status || "none";
          const meta = STATUS_META[status] || STATUS_META.none;
          return (
            <div key={it.kind} className="kd9-card p-6" data-testid={`mi-solicitud-${it.kind}`}>
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ background: `${meta.color}22`, border: `1px solid ${meta.color}55` }}
                >
                  <meta.Icon size={20} style={{ color: meta.color }} />
                </div>
                <div>
                  <div className="font-cinzel kd9-gold-text text-lg tracking-wider">
                    {it.title.toUpperCase()}
                  </div>
                  <div className="font-mont text-[11px]" style={{ color: "var(--kd9-text-soft)" }}>
                    {it.desc}
                  </div>
                </div>
              </div>

              <div
                className="rounded-xl border px-4 py-3 mb-4"
                style={{
                  borderColor: `${meta.color}55`,
                  background: `${meta.color}10`,
                }}
              >
                <div
                  className="text-[10px] font-mont tracking-[0.3em] uppercase mb-1"
                  style={{ color: "var(--kd9-text-soft)" }}
                >
                  Estado actual
                </div>
                <div className="font-mont text-sm font-semibold" style={{ color: meta.color }}>
                  {meta.label}
                </div>
              </div>

              {status === "pending" && (
                <p className="font-cormorant text-base" style={{ color: "var(--kd9-text-soft)" }}>
                  Tu solicitud está siendo revisada por el equipo KD9. Te notificaremos cuando haya una decisión.
                </p>
              )}
              {status === "more_info" && (
                <div>
                  <p className="font-cormorant text-base mb-2" style={{ color: "var(--kd9-text)" }}>
                    El equipo KD9 necesita más información:
                  </p>
                  {req?.notes && (
                    <p className="font-cormorant text-base italic mb-3" style={{ color: "var(--kd9-text-soft)" }}>
                      “{req.notes}”
                    </p>
                  )}
                </div>
              )}
              {status === "approved" && (
                <p className="font-cormorant text-base" style={{ color: "var(--kd9-text)" }}>
                  ¡Solicitud aprobada! Cierra sesión e ingresa nuevamente para acceder al nuevo panel.
                </p>
              )}
              {status === "rejected" && (
                <div>
                  <p className="font-cormorant text-base mb-2" style={{ color: "var(--kd9-text-soft)" }}>
                    Tu solicitud fue rechazada.
                  </p>
                  {req?.notes && (
                    <p className="font-cormorant text-base italic mb-3" style={{ color: "var(--kd9-text-soft)" }}>
                      “{req.notes}”
                    </p>
                  )}
                  <button
                    onClick={() => send(it.kind, it.endpoint)}
                    className="kd9-btn-primary px-5 py-2.5 rounded-lg text-[10px] flex items-center gap-2"
                    data-testid={`mi-solicitud-resend-${it.kind}`}
                  >
                    <RotateCcw size={12} /> Volver a enviar
                  </button>
                </div>
              )}
              {it.kind === "consultant" && (status === "none" || status === "rejected") && (
                <div className="mb-4 space-y-3">
                  <p className="font-mont text-xs kd9-gold-text">Solicitud de Consultor KD9. La elevación a Premium es posterior y exclusiva de Dirección.</p>
                  <div className="flex items-center gap-3">
                    {consultantPhotoPreview && <img src={consultantPhotoPreview} alt="Fotografía frontal" className="w-20 h-20 rounded-xl object-cover" />}
                    <label className="kd9-btn-outline px-4 py-3 rounded-lg text-[10px] cursor-pointer">
                      Fotografía frontal
                      <input type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file || file.size > 5 * 1024 * 1024) { setError("La fotografía debe pesar máximo 5 MB."); return; }
                        const reader = new FileReader();
                        reader.onload = () => { setConsultantPhotoData(String(reader.result || "")); setConsultantPhotoPreview(String(reader.result || "")); setError(""); };
                        reader.readAsDataURL(file);
                      }} />
                    </label>
                  </div>
                  <p className="font-cormorant text-sm" style={{ color: "var(--kd9-text-soft)" }}>JPG, PNG o WebP. Debe ser frontal, reciente, nítida y sin filtros.</p>
                </div>
              )}
              {status === "none" && (
                <button
                  onClick={() => send(it.kind, it.endpoint)}
                  className="kd9-btn-primary px-5 py-2.5 rounded-lg text-[10px] flex items-center gap-2"
                  data-testid={`mi-solicitud-send-${it.kind}`}
                >
                  Enviar solicitud
                </button>
              )}
            </div>
          );
        })}
      </div>
    </PageShell>
  );
};

export default MisSolicitudes;
